import { cloneDeep } from "lodash";
import {
  JOB_APPLICATION_MATCH_STATUS,
  LANGUAGE_KEY_NAMES,
  SEARCH_BY_ATTRIBUTES,
  SEARCH_SOURCE,
  SKILL_TYPES,
  SOCIAL_LINK_TYPES,
  USER_STATUS,
} from "lookup";
import api from "apiSingleton";
import { convertMilesToMeters } from "utils/common";

const ALGOLIA_RESPONSE_CONFIG = JSON.parse(
  process.env.REACT_APP_ALGOLIA_RESPONSE_CONFIG || "{}"
);

export const getFilteredUserIDs = (
  matches,
  hideSkipped,
  hideRejected,
  hideMatchedAndApplied
) => {
  const statusGroupsToHide = [];

  if (hideSkipped) {
    statusGroupsToHide.push(JOB_APPLICATION_MATCH_STATUS.SKIPPED);
  }

  if (hideRejected) {
    statusGroupsToHide.push(
      JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMEMBER,
      JOB_APPLICATION_MATCH_STATUS.REJECTEDBYCUSTOMER,
      JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER
    );
  }

  if (hideMatchedAndApplied) {
    statusGroupsToHide.push(
      JOB_APPLICATION_MATCH_STATUS.MATCHED,
      JOB_APPLICATION_MATCH_STATUS.APPLIED,
      JOB_APPLICATION_MATCH_STATUS.OFFERSENT
    );
  }

  const filteredUserIds = matches
    .filter((match) => statusGroupsToHide.includes(match.status))
    .map((match) => match.userId);

  return filteredUserIds;
};

const algoliaResponsesCache = new Map();
const algoliaRequestsCache = new Map();

const prepareLangs = (languages = undefined, languageLevels = undefined) => {
  const output = [];

  switch (true) {
    case !!languages && !!languageLevels: {
      languages.forEach((lang) => {
        languageLevels.forEach((level) => {
          output.push(`knownLanguages_str:${lang}_${level}`);
        });
      });

      break;
    }

    case !!languages: {
      languages.forEach((lang) => {
        output.push(`knownLanguages.language:${lang}`);
      });

      break;
    }

    case !!languageLevels: {
      languageLevels.forEach((level) => {
        output.push(`knownLanguages.level:${level}`);
      });

      break;
    }

    default: {
      break;
    }
  }

  return output;
};

const getIsStateApplied = (state) => {
  let isMultirangeApplied = false;
  let isToggleApplied = false;
  let isRefinementListApplied = false;
  let isRangeApplied = false;

  if (state.multiRange) {
    isMultirangeApplied = !!Object.values(state.multiRange).filter(
      (el) => !!el.length
    ).length;
  }

  if (state.toggle) {
    isToggleApplied = !!Object.values(state.toggle).length;
  }

  if (state.refinementList) {
    isRefinementListApplied = !!Object.values(state.refinementList).filter(
      (el) => !!el.length
    ).length;
  }

  if (state.range) {
    isRangeApplied = !!Object.values(state.range).filter(
      (el) => !!el.min || !!el.max
    ).length;
  }

  return (
    isMultirangeApplied ||
    isToggleApplied ||
    isRefinementListApplied ||
    isRangeApplied
  );
};

export const prepareQuery = (searchState, operators) => {
  const filter = {};
  const isStateApplied = getIsStateApplied(searchState);

  if (!isStateApplied) {
    // Default state
    return;
  }

  if (searchState.multiRange) {
    filter.numericFilters = [];

    Object.keys(searchState.multiRange).forEach((key) => {
      const [min, max] = searchState.multiRange[key].split(":");

      if (min && max) {
        filter.numericFilters = [
          ...filter.numericFilters,
          `${key}:${min} TO ${max}`,
        ];
      } else if (min && !max) {
        filter.numericFilters = [...filter.numericFilters, `${key}>=${min}`];
      } else if (!min && max) {
        filter.numericFilters = [...filter.numericFilters, `${key}<=${max}`];
      }
    });
  }

  if (searchState.refinementList || searchState.toggle) {
    const filters = {
      ...(searchState.refinementList || {}),
      ...(searchState.toggle || {}),
    };

    const isThereLangFilters =
      filters[LANGUAGE_KEY_NAMES.language] || filters[LANGUAGE_KEY_NAMES.level];

    const filtersKeys = Object.keys(filters).filter(
      (key) =>
        key !== LANGUAGE_KEY_NAMES.language && key !== LANGUAGE_KEY_NAMES.level
    );

    if (!filter.facetFilters) {
      filter.facetFilters = [];
    }

    if (isThereLangFilters) {
      filter.facetFilters = [
        ...filter.facetFilters,
        prepareLangs(
          filters[LANGUAGE_KEY_NAMES.language],
          filters[LANGUAGE_KEY_NAMES.level]
        ),
      ];
    }

    filtersKeys.forEach((key) => {
      if (typeof filters[key] === "boolean" && !!filters[key]) {
        filter.facetFilters = [
          ...filter.facetFilters,
          `${key}:${filters[key]}`,
        ];
      } else if (!!filters[key]) {
        const operator = operators[key];
        const currentFilter =
          operator && operator === "and"
            ? filters[key].map((el) => `${key}:${el}`)
            : [filters[key].map((el) => `${key}:${el}`)];

        filter.facetFilters = [...filter.facetFilters, ...currentFilter];
      }
    });
  }

  if (searchState.range) {
    filter.numericFilters = [...(filter.numericFilters || [])];

    Object.keys(searchState.range).forEach((key) => {
      const { min, max } = searchState.range[key];

      if (max !== undefined) {
        filter.numericFilters = [
          ...filter.numericFilters,
          `${key}:${min || 0} TO ${max}`,
        ];
      }
    });
  }

  if (!searchState.toggle) {
    if (!filter.facetFilters) {
      filter.facetFilters = [];
    }
  }

  return filter;
};

export const formatAISkillSearchString = (skills) => {
  const skillString = skills
    .filter((skill) => skill.active)
    .map((skill) => {
      return skill.name.includes(" ") ? `"${skill.name}"` : skill.name;
    })
    .join(" ");

  return skillString;
};

const _getSkillsStrFilter = (skillsStr, skillsNameOperator = "AND") => {
  const obj = {};
  const operator = skillsNameOperator.toUpperCase();

  for (const skillStr of skillsStr) {
    const parts = skillStr.split(" > ");

    const key = `${parts[0]}_${parts[1]}`;

    if (obj[key]) {
      obj[key] = [...obj[key], `careers.TKSkillsHierarchy:"${skillStr}"`];
    } else {
      obj[key] = [`careers.TKSkillsHierarchy:"${skillStr}"`];
    }
  }

  const mappedFilter = Object.keys(obj).map((key) => {
    return `(${obj[key].join(" OR ")})`;
  });

  return !!mappedFilter.length
    ? ` AND ${mappedFilter.join(` ${operator} `)}`
    : "";
};

const ALGOLIA_FILTERS = `status:${USER_STATUS.ACTIVE}`;
export const getFilterStr = (searchState, operators) => {
  let filterStr = ALGOLIA_FILTERS;
  if (!searchState.basicSearch) {
    if (!searchState.locationFilter?.aroundRadius) {
      filterStr = `${filterStr}${searchState.locationFilterStr}`;
    }

    const skillsStrFilter = _getSkillsStrFilter(
      searchState.TKSkillsHierarchyExp || [],
      operators["TKSkillsHierarchy"]
    );

    filterStr = `${filterStr}${skillsStrFilter}${
      searchState.placementFilterStr
    }${searchState.yearsOfExperienceFilterStr ?? ""}${
      searchState.BHStatusFilterStr
    }`;
  }

  // Hide-user exclusions only apply in Advanced Search.
  if (searchState.basicSearch) {
    return filterStr;
  }

  // searchkit doesn't handle facetsExcludes correctly hence setting them in the filter
  const filteredObjectIDs =
    searchState?.configProps?.facetsExcludes?.objectID || [];

  const filteredObjectIDsFilter =
    filteredObjectIDs?.length > 0
      ? ` AND (${filteredObjectIDs
          .map((e) => `NOT objectID:"${e}"`)
          .join(" AND ")})`
      : "";

  return `${filterStr}${filteredObjectIDsFilter}`;
};

export function parseSkillsString(input) {
  const hasQuotes = input.includes('"');
  const hasCommas = input.includes(",");

  if (!hasQuotes && !hasCommas) {
    return [input.trim()];
  }

  const regex = /"([^"]+)"|[^"\s,]+(?: [^"\s,]+)*/g;
  const matches = [];
  let match;

  while ((match = regex.exec(input)) !== null) {
    const skill = match[1] || match[0];
    matches.push(skill.trim());
  }

  return matches.filter(Boolean);
}

export function aiSkillsToString(skillString) {
  if (!skillString?.trim()) return [];

  const parsedSkills = parseSkillsString(skillString);

  return parsedSkills.map((term) => ({
    name: term,
    active: true,
    ai: {
      isGenerated: false,
      skillType: SKILL_TYPES.REQUIRED,
    },
  }));
}

export function formatAISkills(termsArray, skillType) {
  if (!termsArray) {
    return [];
  }

  const returnedTermsArray = termsArray.map((term) => {
    return {
      name: term,
      active: true,
      ai: {
        isGenerated: true,
        skillType,
      },
    };
  });

  return returnedTermsArray;
}

// Parse optional skills string into array format for Algolia
// Examples:
// Input: "term1 term2" -> Output: ["term1", "term2"]
// Input: 'term1 "phrase one"' -> Output: ["term1", "phrase one"]
function _parseStringToTermsArray(optionalSkillsString) {
  if (!optionalSkillsString) {
    return [];
  }

  // Split the string by spaces but respect quoted phrases
  const result = [];
  let currentTerm = "";
  let inQuote = false;

  for (let i = 0; i < optionalSkillsString.length; i++) {
    const char = optionalSkillsString[i];

    if (char === '"') {
      inQuote = !inQuote;
      continue; // Skip the quote character
    }

    if (char === " " && !inQuote) {
      if (currentTerm) {
        result.push(currentTerm);
        currentTerm = "";
      }
    } else {
      currentTerm += char;
    }
  }

  // Add the last term if there is one
  if (currentTerm) {
    result.push(currentTerm);
  }

  return result;
}

export const getCleanedOptionalWordsArray = (searchState) => {
  const requiredString = formatAISkillSearchString(
    searchState?.skills?.required || []
  );

  const requiredArray = _parseStringToTermsArray(requiredString) || [];

  const optionalString = formatAISkillSearchString(
    searchState?.skills?.optional || []
  );

  const optionalArray = _parseStringToTermsArray(optionalString) || [];

  // Clean optional words by removing required terms (case-insensitive)
  return optionalArray
    .map((optWord) => {
      let cleanedWord = optWord;
      // For each required word, remove it from the optional word if present
      for (const reqWord of requiredArray) {
        // Create a regex with word boundaries and case insensitivity
        const regex = new RegExp(
          `\\b${reqWord.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`,
          "gi"
        );
        cleanedWord = cleanedWord.replace(regex, "").trim();
      }
      return cleanedWord;
    })
    .filter((word) => word && word.trim().length > 0)
    .map((word) => (word.includes(" ") ? `"${word}"` : word));
};

const _getSplittedArray = (wordsArray) => {
  return [
    ...new Set(
      wordsArray
        .flatMap((w) => {
          if (w && w.trim().startsWith("-")) {
            return w;
          }

          return w.split(" ");
        })
        .map((x) => x.trim().toLowerCase())
        .filter((x) => x && x !== "and" && x !== "or")
    ),
  ];
};

export const getSplittedQueries = (searchState) => {
  // don't do anything for searchState.basicSearch || searchState?.customSearchQuery
  if (searchState.basicSearch || searchState?.customSearchQuery) {
    return;
  }

  const requiredString = formatAISkillSearchString(
    searchState?.skills?.required || []
  );

  const requiredArray = _parseStringToTermsArray(requiredString) || [];

  const splittedRequiredArray = _getSplittedArray(requiredArray);

  const optionalString = formatAISkillSearchString(
    searchState?.skills?.optional || []
  );

  const optionalArray = _parseStringToTermsArray(optionalString) || [];

  const splittedOptionalArray = _getSplittedArray(optionalArray)
    .map((optWord) => {
      let cleanedWord = optWord;
      // For each required word, remove it from the optional word if present
      for (const reqWord of splittedRequiredArray) {
        // Create a regex with word boundaries and case insensitivity
        const regex = new RegExp(
          `\\b${reqWord.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`,
          "gi"
        );
        cleanedWord = cleanedWord.replace(regex, "").trim();
      }
      return cleanedWord;
    })
    .filter((word) => word && word.trim().length > 0);

  let query = [splittedOptionalArray.join(" "), splittedRequiredArray.join(" ")]
    .filter((e) => e && e.length > 0)
    .join(" ");

  // "Community boost" is defined in the backend when the query is built and SearchKit calls `getQuery`.
  // Since the function is only invoked when the query has a value, we set it to " " (a single space) to ensure it runs.
  // https://github.com/searchkit/searchkit/blob/6387efde8c7020620d8f2d512c8838e7e164e952/packages/searchkit/src/transformRequest.ts#L222C46-L224
  if (
    !query &&
    searchState.communityBoost &&
    searchState.communityBoost !== "NONE"
  ) {
    query = " ";
  }

  return {
    requiredWords: splittedRequiredArray,
    optionalWords: splittedOptionalArray,
    query,
  };
};

export const getCommunityBoost = (searchState, platformUsersIndexName) => {
  if (searchState.basicSearch) {
    return null;
  }

  // No boost if sort by is not default
  return !searchState.sortBy || searchState.sortBy === platformUsersIndexName
    ? searchState.communityBoost
    : null;
};

export const getSearchStateQuery = (searchState) => {
  if (searchState.basicSearch) {
    /// "Community boost" is defined in the backend when the query is built and SearchKit calls `getQuery`.
    // Since the function is only invoked when the query has a value, we set it to " " (a single space) to ensure it runs.
    // https://github.com/searchkit/searchkit/blob/6387efde8c7020620d8f2d512c8838e7e164e952/packages/searchkit/src/transformRequest.ts#L222C46-L224
    return searchState?.customSearchQuery || " ";
  }

  if (searchState?.customSearchQuery) {
    // "Community boost" is defined in the backend when the query is built and SearchKit calls `getQuery`.
    // Since the function is only invoked when the query has a value, we set it to " " (a single space) to ensure it runs.
    // https://github.com/searchkit/searchkit/blob/6387efde8c7020620d8f2d512c8838e7e164e952/packages/searchkit/src/transformRequest.ts#L222C46-L224
    return searchState?.customSearchQuery || " ";
  }

  const cleanedOptionalWordsArray = getCleanedOptionalWordsArray(searchState);

  const textSearch = [
    cleanedOptionalWordsArray.join(" "),
    formatAISkillSearchString(searchState?.skills?.required || []),
  ];

  const query = textSearch.filter((e) => e && e.length > 0).join(" ");

  // "Community boost" is defined in the backend when the query is built and SearchKit calls `getQuery`.
  // Since the function is only invoked when the query has a value, we set it to " " (a single space) to ensure it runs.
  // https://github.com/searchkit/searchkit/blob/6387efde8c7020620d8f2d512c8838e7e164e952/packages/searchkit/src/transformRequest.ts#L222C46-L224
  if (
    !query &&
    searchState.communityBoost &&
    searchState.communityBoost !== "NONE"
  ) {
    return " ";
  }

  return query;
};

export const getRequiredWords = (searchState) => {
  if (searchState.basicSearch || searchState?.customSearchQuery) {
    return null;
  }

  return formatAISkillSearchString(searchState?.skills?.required || []);
};

export const getGeoLocation = (searchState) => {
  const geoLocation = {};

  if (
    !searchState.basicSearch &&
    searchState.locationFilter?.aroundLatLng &&
    searchState.locationFilter?.aroundRadius &&
    searchState.locationFilter?.cityName
  ) {
    geoLocation.aroundLatLng = searchState.locationFilter?.aroundLatLng;
    geoLocation.aroundRadius = convertMilesToMeters(
      searchState.locationFilter.aroundRadius
    );
  }

  return geoLocation;
};

export const getSearchConfigureParams = (
  searchState,
  operators,
  platformUsersIndexName
) => {
  const configPropsRest = { ...(searchState?.configProps ?? {}) };
  delete configPropsRest.facetsExcludes;

  return {
    filters: getFilterStr(searchState, operators),
    restrictSearchableAttributes: searchState.basicSearch
      ? ["email", "username", "fullName", "sfdcExternalID"]
      : SEARCH_BY_ATTRIBUTES[0].attributes,
    attributesToHighlight: searchState.basicSearch
      ? []
      : SEARCH_BY_ATTRIBUTES[0].attributes,
    ...getGeoLocation(searchState),
    ...(searchState.basicSearch ? {} : configPropsRest),
    removeWordsIfNoResults: searchState?.configProps?.removeWordsIfNoResults,
    requiredWords: getRequiredWords(searchState),
    optionalWords: searchState.basicSearch
      ? []
      : getCleanedOptionalWordsArray(searchState),
    query: getSearchStateQuery(searchState),
    communityBoost: getCommunityBoost(searchState, platformUsersIndexName),
    ...((process.env.REACT_APP_QUERY_PHRASE_SPLIT_ENABLED === "true" &&
      getSplittedQueries(searchState)) ||
      {}),
  };
};

const mapHit = (hit, matches, jobId) => {
  const clonedHit = cloneDeep(hit);
  let associatedMatch = matches.find((m) => m.userId === hit.id);

  // We are no longer fetching all job matches on page load.
  // So we are falling back on algolia hit record (hit.matches) for getting the associated match.
  if (!associatedMatch && hit?.objectID && hit?.matches) {
    associatedMatch = hit.matches.find((m) => m.jobOpportunityId === jobId);
  }

  if (clonedHit?.rankDetails) {
    try {
      clonedHit.rankDetails =
        typeof clonedHit.rankDetails === "string"
          ? JSON.parse(clonedHit.rankDetails)
          : clonedHit.rankDetails;
    } catch (error) {
      console.log("Invalid rankDetails Format in hit");
      delete clonedHit.rankDetails;
    }
  }

  clonedHit.associatedMatch = associatedMatch;
  clonedHit.socialLinks = clonedHit.socialLinks
    ? clonedHit.socialLinks.filter(
        (s) => !!s.value && s.type !== SOCIAL_LINK_TYPES.CALENDAR
      )
    : [];

  return {
    ...clonedHit,
  };
};

export const mapHits = (
  hits,
  matches,
  filterOutHitByMatchStatus = [],
  jobId
) => {
  const mappedHits = hits
    .map((hit) => mapHit(hit, matches, jobId))
    .filter(
      (hit) =>
        !!hit &&
        !filterOutHitByMatchStatus.includes(hit.associatedMatch?.status)
    );

  return mappedHits;
};

// Default ttl cache 1 minute
const CACHE_TTL =
  (ALGOLIA_RESPONSE_CONFIG.responseCacheTTLMin ?? 1) * 60 * 1000;

let currentStreamSearchController = null;

export const abortCurrentStreamSearch = () => {
  currentStreamSearchController?.abort();
  currentStreamSearchController = null;
};

const startStreamSearch = (stream) => {
  if (!stream) {
    return null;
  }

  abortCurrentStreamSearch();

  const controller = new AbortController();
  currentStreamSearchController = controller;

  return controller;
};

const isCurrentStreamSearch = (controller) => {
  return (
    !controller ||
    (controller === currentStreamSearchController && !controller.signal.aborted)
  );
};

const clearCurrentStreamSearch = (controller) => {
  if (controller === currentStreamSearchController) {
    currentStreamSearchController = null;
  }
};

export const getCustomSearchClient = (metadata, readyState, helpers) => {
  return {
    async search(requests) {
      if (readyState && readyState.isReadyForSearch) {
        helpers.handleReadyForSearch(false);
      }

      if (
        !metadata?.reRankHitsForJob &&
        readyState?.isLoadingReRanking &&
        helpers?.handleReRankHitsForJob
      ) {
        helpers.handleReRankHitsForJob(false, "isLoading");
      }

      const params = {
        requests: JSON.stringify(requests),
        searchSource: SEARCH_SOURCE.OPEN_SEARCH,
      };

      if (metadata) {
        params.metadata = JSON.stringify(metadata);
      }

      if (ALGOLIA_RESPONSE_CONFIG.cacheIsDisabled) {
        const results = await api.proxy.search(params, readyState?.stream);

        return results;
      }

      const requestsKey = JSON.stringify({
        requests,
        metadata,
        searchSource: SEARCH_SOURCE.OPEN_SEARCH,
      });

      if (algoliaResponsesCache.has(requestsKey)) {
        const { data, expiryDate, infiniteCache } =
          algoliaResponsesCache.get(requestsKey);
        if (infiniteCache || Date.now() < expiryDate) {
          if (readyState && !readyState.pageIsLoaded) {
            setTimeout(() => {
              helpers.handlePageIsLoaded(true);
            }, 150);
          }

          if (
            metadata?.reRankHitsForJob &&
            readyState?.isLoadingReRanking &&
            helpers?.handleReRankHitsForJob
          ) {
            helpers.handleReRankHitsForJob(false, "isLoading");
          }

          return data;
        } else {
          // Remove expired cache.
          algoliaResponsesCache.delete(requestsKey);
        }
      }

      if (algoliaRequestsCache.has(requestsKey)) {
        return algoliaRequestsCache.get(requestsKey);
      }

      const isPlatformUserIndex =
        helpers?.platformUsersIndexName &&
        JSON.stringify({ requests }).includes(helpers.platformUsersIndexName);

      if (readyState && isPlatformUserIndex && !readyState.isReadyForSearch) {
        return;
      }

      // Create a promise to perform the API call.
      const searchPromise = (async () => {
        const streamSearch = startStreamSearch(readyState?.stream);

        try {
          if (metadata?.reRankHitsForJob && helpers?.handleReRankHitsForJob) {
            helpers.handleReRankHitsForJob(true, "isLoading");
          }

          let results;

          try {
            results = await api.proxy.search(params, readyState?.stream, {
              signal: streamSearch?.signal,
            });
          } catch (err) {
            if (streamSearch?.signal?.aborted) {
              return;
            }

            console.log(err);

            if (helpers?.handlePageIsLoaded) {
              setTimeout(() => {
                helpers.handlePageIsLoaded(true);
              }, 150);
            }

            if (metadata?.reRankHitsForJob && helpers?.handleReRankHitsForJob) {
              helpers.handleReRankHitsForJob(false, "isLoading");
            }

            return;
          }

          const parsedResults = results;

          if (!isCurrentStreamSearch(streamSearch)) {
            return;
          }

          const isPlatformUserIndex =
            helpers?.platformUsersIndexName &&
            requestsKey.includes(helpers?.platformUsersIndexName);

          if (isPlatformUserIndex || helpers?.infiniteCache) {
            // Cache the result along with its expiryDate.
            algoliaResponsesCache.set(requestsKey, {
              data: parsedResults,
              expiryDate: Date.now() + CACHE_TTL,
              infiniteCache: helpers?.infiniteCache,
            });

            if (readyState && !readyState.pageIsLoaded) {
              setTimeout(() => {
                helpers.handlePageIsLoaded(true);
              }, 150);
            }
          }

          if (metadata?.reRankHitsForJob && helpers?.handleReRankHitsForJob) {
            helpers.handleReRankHitsForJob(false, "isLoading");
          }

          return parsedResults;
        } finally {
          clearCurrentStreamSearch(streamSearch);

          // Remove the in-progress request once done.
          algoliaRequestsCache.delete(requestsKey);
        }
      })();

      // Store/cache the in-progress request.
      algoliaRequestsCache.set(requestsKey, searchPromise);

      return searchPromise;
    },

    async searchForFacetValues(requests) {
      const parsedResults = await api.proxy.search({
        requests: JSON.stringify(requests),
        searchSource: SEARCH_SOURCE.OPEN_SEARCH,
        sffv: true,
      });

      // facet results are returned within results
      return parsedResults.results;
    },
  };
};
